import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import NewColors from "../../../shared/theme/NewColors";
import ExerciseNameLink from "../../plan/components/ExerciseNameLink";
import ExerciseProgressChart from "./ExerciseProgressChart";
import { formatWeight, formatReps } from "../../../lib/formatNumber";
import { CLIENT_ADDED_ACCENT_PURPLE } from "../lib/workoutReportMutations";

function getReps(set) {
  if (set.completedReps && set.completedReps > 0) return set.completedReps;
  if (set.completed === true) return set.reps ?? 0;
  return 0;
}

function getExerciseAccentColor(exercise, theme) {
  if (exercise?.skipped === true) return NewColors.status.warning;
  if (exercise?.clientAdded === true && exercise?.completed !== true) {
    return CLIENT_ADDED_ACCENT_PURPLE;
  }
  if (exercise?.completed === true) return theme.palette.primary.main;
  return theme.palette.grey[400];
}

function ExerciseStatusChips({ exercise }) {
  const chips = [];
  if (exercise?.skipped === true) {
    chips.push(<Chip key="skipped" label="Skipped" size="small" color="warning" variant="outlined" />);
  }
  if (exercise?.clientAdded === true) {
    chips.push(
      <Chip
        key="client-added"
        label="Added in session"
        size="small"
        variant="outlined"
        sx={{
          borderColor: CLIENT_ADDED_ACCENT_PURPLE,
          color: CLIENT_ADDED_ACCENT_PURPLE,
          backgroundColor: `${CLIENT_ADDED_ACCENT_PURPLE}14`,
          fontWeight: 600,
        }}
      />,
    );
  }
  if (!chips.length) return null;
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.75 }}>
      {chips}
    </Stack>
  );
}

function SetInfo({ exercise, weightUnit = "lbs" }) {
  if (!Array.isArray(exercise?.sets) || exercise.sets.length === 0) return null;
  return (
    <Box sx={{ pl: 1 }}>
      {exercise.sets.map((set, i) => {
        const skippedSet = set?.skipped === true;
        let detail = "";
        if (!skippedSet) {
          const reps = formatReps(getReps(set));
          const wt = formatWeight(set?.weight ?? set?.workingWeight ?? "");
          detail = reps || wt ? ` — ${reps ? `${reps} reps` : ""}${wt ? `${reps ? " · " : ""}${wt} ${weightUnit}` : ""}` : "";
        } else {
          const targetReps =
            typeof set?.reps === "number"
              ? set.reps
              : typeof set?.reps === "string"
                ? Number.parseFloat(set.reps)
                : null;
          const wt = formatWeight(set?.weight ?? set?.workingWeight ?? "");
          const tgtStr = Number.isFinite(targetReps) ? `${Math.round(targetReps)} reps` : "";
          const wtStr = wt ? `${tgtStr ? `${tgtStr} · ` : ""}${wt} ${weightUnit}` : tgtStr;
          detail = wtStr ? ` (planned ${wtStr})` : "";
        }

        let status = "";
        if (skippedSet) status = "Skipped";
        else if (set?.completed === true) status = "Logged";
        else status = "Not logged";

        return (
          <Typography
            key={i}
            variant="body2"
            color={skippedSet ? "warning.main" : set?.completed === true ? "text.primary" : "text.secondary"}
          >
            Set {i + 1} — {status}
            {detail}
          </Typography>
        );
      })}
    </Box>
  );
}

function ExerciseBlock({ exercise, findById, weightUnit = "lbs", aggregated }) {
  const theme = useTheme();
  const accent = getExerciseAccentColor(exercise, theme);
  const isSkippedExercise = exercise?.skipped === true;
  const sets = exercise.sets ?? [];
  const completeCount = sets.filter((s) => s.completed).length;
  const pctRaw = sets.length > 0 ? (completeCount / sets.length) * 100 : exercise.completed ? 100 : 0;
  const pctRounded = Math.min(100, Math.max(0, Math.round(Number(pctRaw) || 0)));

  const name =
    exercise?.exerciseName ??
    (findById ? findById(exercise?.exercise)?.name : null) ??
    exercise?.exercise ??
    "Exercise";
  const exerciseId = exercise?.exercise ?? exercise?.id ?? null;

  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", sm: "flex-start" },
        gap: 1.5,
        borderLeft: "4px solid",
        borderColor: accent,
        borderRadius: 1,
        backgroundColor: "background.paper",
        p: 1.25,
        opacity: isSkippedExercise ? 0.74 : 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ExerciseStatusChips exercise={exercise} />
        <Box
          sx={{
            color: exercise.completed ? "success.main" : "text.secondary",
            "& a": { color: "inherit" },
            filter: isSkippedExercise ? "grayscale(35%)" : "none",
          }}
        >
          <ExerciseNameLink exerciseId={exercise?.exercise} name={name} variant="body2" />
        </Box>
        <LinearProgress
          variant="determinate"
          value={isSkippedExercise ? 0 : pctRounded}
          sx={{
            my: 0.75,
            height: 6,
            borderRadius: 1,
            opacity: isSkippedExercise ? 0.35 : 1,
          }}
        />
        <SetInfo exercise={exercise} weightUnit={weightUnit} />
      </Box>
      <Box sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, minWidth: 0 }}>
        <ExerciseProgressChart exerciseId={exerciseId} aggregated={aggregated} weightUnit={weightUnit} />
      </Box>
    </Box>
  );
}

function SuperSetExercise({ exercise, findById, weightUnit = "lbs", aggregated }) {
  return (
    <Box
      sx={{
        borderLeft: "4px solid",
        borderColor: "secondary.main",
        pl: 2,
        mb: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: "action.hover",
          py: 0.5,
          px: 1,
          borderRadius: 0.5,
          mb: 0.5,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          {exercise.superSetName || "Super Set"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
          Perform together per set
        </Typography>
      </Box>
      {(exercise.sets ?? []).map((set, setIdx) => (
        <Box key={setIdx} sx={{ pl: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Set {setIdx + 1}
          </Typography>
          {(set.exercises ?? []).map((ex, exIdx) => (
            <ExerciseBlock
              key={ex.id ?? `${setIdx}-${exIdx}`}
              exercise={ex}
              findById={findById}
              weightUnit={weightUnit}
              aggregated={aggregated}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function WorkoutDetail({
  workout,
  findById,
  weightUnit = "lbs",
  aggregated,
  organizationId,
  clientId,
  onEditWorkout,
}) {
  if (!workout?.reportData?.exercises?.length) {
    return (
      <Box sx={{ p: 2, backgroundColor: NewColors.sectionBackground, borderRadius: 1 }}>
        <Typography color="text.secondary">No exercise data for this workout.</Typography>
      </Box>
    );
  }
  const protocolName = workout.reportData?.protocol?.name ?? "";
  const splitName = workout.reportData?.split?.name ?? "";
  const dateStr = workout.completedOn
    ? new Date(workout.completedOn).toLocaleDateString()
    : workout.createdOn
      ? new Date(workout.createdOn).toLocaleDateString()
      : "";

  const canLaunchEditor =
    typeof onEditWorkout === "function" && Boolean(organizationId && clientId && workout?.id);

  return (
    <Box
      sx={{
        backgroundColor: NewColors.sectionBackground,
        borderRadius: 2,
        p: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {dateStr}
          {protocolName && ` · ${protocolName}`}
          {splitName && ` · ${splitName}`}
        </Typography>
        {canLaunchEditor ? (
          <Button variant="outlined" size="small" onClick={() => onEditWorkout(workout)} sx={{ alignSelf: { sm: "center" } }}>
            Edit workout
          </Button>
        ) : null}
      </Stack>
      <Divider sx={{ mb: 1 }} />
      {workout.reportData.exercises.map((ex, idx) =>
        ex.type === "super_set" ? (
          <SuperSetExercise key={ex.id ?? idx} exercise={ex} findById={findById} weightUnit={weightUnit} aggregated={aggregated} />
        ) : (
          <ExerciseBlock key={ex.id ?? idx} exercise={ex} findById={findById} weightUnit={weightUnit} aggregated={aggregated} />
        ),
      )}
    </Box>
  );
}
